import React, {useMemo, useRef, useState} from "react";
import {
    Box,
    Button,
    Container,
    Flex,
    Heading,
    Input,
    SimpleGrid,
    Stack,
    Text,
    Textarea
} from "@chakra-ui/react";
import CueBuilder from "./CueBuilder";
import {
    Cue,
    createEditableCue,
    editableCueFromParsed,
    getPlaybackWarnings,
    MAX_CUES,
    ParseMessage,
    parseCuesText,
    serializeEditableCues
} from "./cues";

const CueEditor = () => {
    const [builderCues, setBuilderCues] = useState(() => [createEditableCue()]);
    const [importErrors, setImportErrors] = useState<ParseMessage[]>([]);
    const [importWarnings, setImportWarnings] = useState<ParseMessage[]>([]);
    const [importCues, setImportCues] = useState<Cue[]>([]);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const builderText = useMemo(() => serializeEditableCues(builderCues), [builderCues]);
    const builderAnalysis = useMemo(() => {
        const parsed = parseCuesText(builderText);
        return {
            parsed,
            playbackWarnings: getPlaybackWarnings(parsed.cues)
        };
    }, [builderText]);

    const handleFilePick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const text = typeof reader.result === "string" ? reader.result : "";
            const parsed = parseCuesText(text);
            setImportCues(parsed.cues);
            if (parsed.errors.length > 0) {
                setImportErrors(parsed.errors);
                setImportWarnings(parsed.warnings);
                return;
            }
            const next = parsed.cues.slice(0, MAX_CUES).map(editableCueFromParsed);
            setBuilderCues(next.length > 0 ? next : [createEditableCue()]);
            setImportErrors([]);
            setImportWarnings([]);
            setImportCues(parsed.cues);
        };
        reader.readAsText(file);
        event.target.value = "";
    };

    const handleDownloadBuilder = () => {
        const blob = new Blob([builderText], {type: "text/plain"});
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "cues.txt";
        link.click();
        URL.revokeObjectURL(url);
    };

    const renderValidation = (errors: ParseMessage[], warnings: ParseMessage[], cues: Cue[]) => {
        const getCueLabel = (cueIndex?: number) => {
            if (cueIndex === undefined || cueIndex < 0) {
                return undefined;
            }
            const cue = cues[cueIndex];
            const label = cue?.label?.trim();
            return label && label.length > 0 ? label : `Cue ${cueIndex + 1}`;
        };

        const renderMessage = (item: ParseMessage) => {
            const cueLabel = getCueLabel(item.cueIndex);
            const linePrefix = item.line ? `Line ${item.line}: ` : "";
            const cuePrefix = cueLabel ? `${cueLabel}: ` : "";
            return `${linePrefix}${cuePrefix}${item.message}`;
        };

        return (
            <>
                {errors.length > 0 && (
                    <Box mb={4} borderWidth={"1px"} borderColor={"red.200"} borderRadius={"md"} p={3}>
                        <Text fontWeight={"semibold"} color={"red.600"} mb={2}>Errors</Text>
                        <Stack spacing={1}>
                            {errors.map((error, idx) => (
                                <Text fontSize={"sm"} key={`error_${idx}`} color={"red.600"}>
                                    {renderMessage(error)}
                                </Text>
                            ))}
                        </Stack>
                    </Box>
                )}

                {warnings.length > 0 && (
                    <Box mb={4} borderWidth={"1px"} borderColor={"yellow.200"} borderRadius={"md"} p={3}>
                        <Text fontWeight={"semibold"} color={"yellow.700"} mb={2}>Warnings</Text>
                        <Stack spacing={1}>
                            {warnings.map((warning, idx) => (
                                <Text fontSize={"sm"} key={`warning_${idx}`} color={"yellow.700"}>
                                    {renderMessage(warning)}
                                </Text>
                            ))}
                        </Stack>
                    </Box>
                )}
            </>
        );
    };

    const renderCueSummary = (cues: Cue[]) => {
        if (cues.length === 0) {
            return <Text color={"gray.600"}>No cues parsed yet.</Text>;
        }
        return (
            <Stack spacing={3}>
                {cues.map((cue) => (
                    <Box key={`cue_${cue.index}`} borderWidth={"1px"} borderRadius={"md"} p={3}>
                        <Flex justify={"space-between"} mb={1}>
                            <Text fontWeight={"semibold"}>{cue.label?.trim() ? cue.label : `Cue ${cue.index + 1}`}</Text>
                            <Text fontSize={"sm"} color={"gray.600"}>Line {cue.line}</Text>
                        </Flex>
                        {cue.label?.trim() ? (
                            <Text fontSize={"sm"} color={"gray.600"}>Cue {cue.index + 1}</Text>
                        ) : null}
                        <Text fontSize={"sm"}>Action: {cue.action || "unknown"}</Text>
                        <Text fontSize={"sm"}>Trigger: {cue.trigger || "time"}</Text>
                        {cue.trigger === "button" && (
                            <Text fontSize={"sm"}>Trigger value: {cue.triggerValue || "(any)"}</Text>
                        )}
                        {cue.file !== undefined && (
                            <Text fontSize={"sm"}>File: {cue.file === "" ? "(all files)" : cue.file}</Text>
                        )}
                        {cue.atMs !== undefined && (
                            <Text fontSize={"sm"}>at_ms: {cue.atMs}</Text>
                        )}
                        {cue.afterMs !== undefined && (
                            <Text fontSize={"sm"}>after_ms: {cue.afterMs}</Text>
                        )}
                        {cue.gain !== undefined && (
                            <Text fontSize={"sm"}>gain: {cue.gain}</Text>
                        )}
                        {cue.fadeIn !== undefined && (
                            <Text fontSize={"sm"}>fadeIn: {cue.fadeIn}</Text>
                        )}
                        {cue.fadeOut !== undefined && (
                            <Text fontSize={"sm"}>fadeOut: {cue.fadeOut}</Text>
                        )}
                    </Box>
                ))}
            </Stack>
        );
    };

    return (
        <Container maxWidth={"container.xl"} py={8}>
            <Heading size={"lg"} mb={2}>SoundSpots Cue Editor</Heading>
            <Text color={"gray.600"} mb={6}>
                Import a cues file, validate it, and export a clean cues.txt for the SoundSpots Cue device.
            </Text>
            <SimpleGrid columns={{base: 1, lg: 2}} spacing={8}>
                <Box>
                    <Flex gap={3} wrap={"wrap"} mb={3}>
                        <Button onClick={() => fileInputRef.current?.click()} colorScheme={"teal"}>Import cues.txt</Button>
                        <Button
                            onClick={handleDownloadBuilder}
                            colorScheme={"teal"}
                            variant={"outline"}
                            isDisabled={builderText.trim() === ""}
                        >
                            Download cues.txt
                        </Button>
                    </Flex>
                    <Input
                        ref={fileInputRef}
                        type={"file"}
                        accept={".txt"}
                        display={"none"}
                        onChange={handleFilePick}
                    />
                    {(importErrors.length > 0 || importWarnings.length > 0) ? (
                        <Box mb={4}>
                            <Text fontWeight={"semibold"} mb={2}>Import issues</Text>
                            {renderValidation(importErrors, importWarnings, importCues)}
                        </Box>
                    ) : null}
                    <CueBuilder cues={builderCues} onChange={setBuilderCues}/>
                </Box>
                <Box>
                    <Heading size={"md"} mb={3}>Generated cues.txt</Heading>
                    <Textarea
                        value={builderText}
                        isReadOnly
                        minHeight={"260px"}
                        fontFamily={"monospace"}
                        fontSize={"sm"}
                        mb={4}
                    />
                    {renderValidation(builderAnalysis.parsed.errors, [
                        ...builderAnalysis.parsed.warnings,
                        ...builderAnalysis.playbackWarnings
                    ], builderAnalysis.parsed.cues)}
                </Box>
            </SimpleGrid>
        </Container>
    );
};

export default CueEditor;
